import React from "react";
import classNames from "classnames";
import DateDisplay from "../../DateDisplay.js";
import { Button } from "../..";
import { Modal } from "react-bootstrap";
import { ThemedSpan } from "../../utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useFetchDocumentSignatures } from "./hooks/useFetchDocumentSignatures";
import { DocumentSignatureViewType } from "../../../types/DocumentSignatureType";
import { OpenDocumentSignaturesDisplayModalProps, OpenDocumentSignaturesDisplayModalOptions } from "./types";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { ModalMessageContainer } from "../../messages/ModalMessageContainer";
import ThemedModal from "../../utilities/ThemedModal";

const DocumentSignaturesDisplayModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    documentId,
    show,
    size = "lg",
    ...rest
} : Readonly<OpenDocumentSignaturesDisplayModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const { data, error, isLoading } = useFetchDocumentSignatures(documentId);

    const onClose = () => cancel!();

    const hasError = !isLoading && (error || !data || data.length < 1);
    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>View Document Signatures</Modal.Title>
            </ThemedModal.Header>
            <Modal.Body ref={bodyAnimation} className={classNames({ 'p-0': hasError })}>
                {isLoading && (
                    <div>LOADING</div>
                )}
                {hasError && (
                    <ModalMessageContainer 
                        variant={error ? 'danger' : 'muted'} 
                        text="There was an error fetching the data, or data could not be found." 
                    />
                )}
                {!isLoading && !hasError && data.map((el: DocumentSignatureViewType) => (
                    <div key={el.signatureId} className="card">
                        <div className="card-body">
                            <p>
                                <span className="fw-bold me-1">Signature Requirement:</span>
                                <span className="me-1">{el.clientAccountName}</span>
                            </p>
                            <div className="row">
                                <div className="col-4">
                                    <span className="fw-bold me-1">Status:</span>
                                    <ThemedSpan variant={el.signedOnDate === null ? 'danger' : 'success'}>
                                        {el.signedOnDate === null ? 'Unsigned' : 'Signed'}
                                    </ThemedSpan>
                                </div>
                                {el.signedOnDate !== null && (
                                    <>
                                        <div className="col-4">
                                            <span className="fw-bold me-1">Date Signed:</span>
                                            <DateDisplay format="YYYY-MM-DD">{el.signedOnDate}</DateDisplay>
                                        </div>
                                        <div className="col-4">
                                            <span className="fw-bold me-1">IP:</span>
                                            <span>{el.signedByIpv4}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            {el.signedOnDate !== null && (
                                <>
                                    <div className="row my-3">
                                        <div className="col">
                                            <span className="fw-bold me-1">Print:</span>
                                            <span>{el.printName}</span>
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-center">
                                        <img src={`data:image/png;base64,${el.signature}`} alt="signature_image" width="256px" height="128px" />
                                    </div>
                                </>
                                
                            )}
                        </div>
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" type="button" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
};

export function openDocumentSignaturesDisplayModal(options: OpenDocumentSignaturesDisplayModalOptions) : Promise<void> {
    return createConfirmation<OpenDocumentSignaturesDisplayModalProps, void>(confirmable(DocumentSignaturesDisplayModal))({
        documentId: options.documentId,
        dialogId: 'document_signatures_modal'
    });
}